'use client';

import React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useUpdateUser } from '@kit/supabase/hooks/use-update-user-mutation';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { resolveAuthErrorMessage } from '../../lib/auth-error-message';
import { PasswordResetSchema } from '../../schemas/password-reset.schema';
import { NativeAuthButton } from './native-auth-button';
import { NativeAuthShell } from './native-auth-shell';
import { NativeGroupedInput, NativeInputRow } from './native-grouped-input';

export function NativeUpdatePasswordPage({
  logoUrl,
  accountName,
  primaryColor,
  secondaryColor,
  appBackgroundColor = '#FFFFFF',
  appTextColor = '#1C1C1C',
  redirectTo,
}: {
  logoUrl?: string | null;
  accountName?: string;
  primaryColor: string;
  secondaryColor: string;
  appBackgroundColor?: string;
  appTextColor?: string;
  redirectTo: string;
}) {
  const { t } = useTranslation();
  const updateUser = useUpdateUser();

  const isDark = isDarkColor(appBackgroundColor);
  const inputClass = isDark
    ? 'w-full bg-transparent text-base text-white outline-none placeholder:text-white/40'
    : 'w-full bg-transparent text-base text-gray-900 outline-none placeholder:text-gray-400';

  const form = useForm({
    resolver: zodResolver(PasswordResetSchema),
    defaultValues: { password: '', repeatPassword: '' },
  });

  const handleSubmit = async (data: z.infer<typeof PasswordResetSchema>) => {
    try {
      await updateUser.mutateAsync({
        password: data.password,
        redirectTo,
      });

      toast.success(t('account:updatePasswordSuccessMessage'));
      window.location.href = redirectTo;
    } catch (error) {
      // Surface the real reason (e.g. "can't reuse current password",
      // "link expired") instead of a flat generic message.
      toast.error(resolveAuthErrorMessage(error, t));
    }
  };

  return (
    <NativeAuthShell
      logoUrl={logoUrl}
      accountName={accountName}
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
      appBackgroundColor={appBackgroundColor}
      appTextColor={appTextColor}
      showBackButton
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h2
            className="text-xl font-semibold"
            style={{ color: appTextColor }}
          >
            <Trans i18nKey="auth:updatePassword" defaults="Update Password" />
          </h2>
          <p
            className="text-sm"
            style={{ color: `${appTextColor}80` }}
          >
            <Trans
              i18nKey="auth:updatePasswordSubheading"
              defaults="Enter your new password below."
            />
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-5"
          >
            <NativeGroupedInput appBackgroundColor={appBackgroundColor}>
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <NativeInputRow appTextColor={appTextColor}>
                        <input
                          type="password"
                          autoComplete="new-password"
                          placeholder={t('auth:passwordPlaceholder', 'Password')}
                          disabled={updateUser.isPending}
                          className={inputClass}
                          data-test="native-new-password-input"
                          {...field}
                        />
                      </NativeInputRow>
                    </FormControl>
                    <FormMessage className="px-1 text-red-400" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="repeatPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <NativeInputRow appTextColor={appTextColor}>
                        <input
                          type="password"
                          autoComplete="new-password"
                          placeholder={t('auth:repeatPasswordPlaceholder', 'Repeat Password')}
                          disabled={updateUser.isPending}
                          className={inputClass}
                          data-test="native-repeat-password-input"
                          {...field}
                        />
                      </NativeInputRow>
                    </FormControl>
                    <FormMessage className="px-1 text-red-400" />
                  </FormItem>
                )}
              />
            </NativeGroupedInput>

            <NativeAuthButton
              type="submit"
              disabled={updateUser.isPending}
              loading={updateUser.isPending}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              appTextColor={appTextColor}
            >
              <Trans i18nKey="auth:updatePassword" defaults="Update Password" />
            </NativeAuthButton>
          </form>
        </Form>
      </div>
    </NativeAuthShell>
  );
}

function isDarkColor(hex: string): boolean {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}
